import { useId, useState } from 'react';
import { Download, FileText, Globe } from 'lucide-react';
import type { BrandImportJobView, BrandImportSourceStatus } from '@mg/shared';
import { Button } from '../../../components/ui/Button';
import { downloadBrandDocument } from '../../brand/download';
import { pageReasonText } from '../../brand/importSummary';

/** "Oct 30, 2026"; the text as given when it is not a date. */
export function shortDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

const DOCUMENT_STATUS_TEXT: Record<BrandImportSourceStatus, string> = {
  read: 'Read',
  pending: 'Reading…',
  failed: "Couldn't read this file",
};

export const DOWNLOAD_FAILED_TEXT = "Couldn't download this file. Try again.";

/** Only a web address is ever a link here. */
const isWebAddress = (url: string) => /^https?:\/\//i.test(url);

/** The sources of the latest import have something to list: a document, or web pages (Release 4). */
export function hasSources(job: Pick<BrandImportJobView, 'document' | 'pages'>): boolean {
  return job.document !== null || job.pages.length > 0;
}

/**
 * "Sources from your brand's last onboarding" (spec:190), under the notice box once onboarding has
 * started: the latest import's document and web pages, read-only. Customers add or change sources only
 * by starting Brand Setup again.
 * - Document: its name, the upload date and whether it was read, with an icon-only Download (any
 *   member: the API checks access, E32).
 * - Web pages (Release 4): each address a link that opens in a new tab with noopener and noreferrer,
 *   failed pages included so the customer can check them, then the scan date and Read or the reason.
 */
export function SourcesList({ tenantId, job }: { tenantId: string; job: BrandImportJobView }) {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} className="space-y-3">
      <h4 id={headingId} className="text-sm font-semibold text-ink-strong">
        Sources from your brand's last onboarding · {shortDate(job.createdAt)}
      </h4>
      {job.document && <DocumentSource tenantId={tenantId} jobId={job.id} document={job.document} />}
      {job.pages.length > 0 && (
        <div>
          <h5 className="text-xs font-semibold uppercase tracking-wide text-ink-muted">Web pages</h5>
          <ul className="mt-2 space-y-2">
            {job.pages.map((page) => (
              <li key={page.url} className="flex items-start gap-3">
                <Globe className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" aria-hidden />
                <div className="min-w-0 flex-1">
                  {isWebAddress(page.url) ? (
                    <a
                      href={page.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="focus-ring break-all rounded text-sm font-medium text-link hover:text-action-hover hover:underline"
                    >
                      {page.url}
                      <span className="sr-only"> (opens in a new tab)</span>
                    </a>
                  ) : (
                    <span className="break-all text-sm font-medium text-ink">{page.url}</span>
                  )}
                  <p className="text-xs text-ink-muted">
                    {page.scannedAt ? `Scanned ${shortDate(page.scannedAt)} · ` : ''}
                    {page.status === 'read'
                      ? 'Read'
                      : page.status === 'pending'
                        ? 'Reading…'
                        : page.reason
                          ? pageReasonText(page.reason)
                          : "Couldn't read this page"}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function DocumentSource({
  tenantId,
  jobId,
  document,
}: {
  tenantId: string;
  jobId: string;
  document: NonNullable<BrandImportJobView['document']>;
}) {
  const [downloading, setDownloading] = useState(false);
  const [failed, setFailed] = useState(false);

  const download = async () => {
    setDownloading(true);
    setFailed(false);
    try {
      await downloadBrandDocument(tenantId, jobId, document.fileName);
    } catch {
      setFailed(true);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div>
      <h5 className="text-xs font-semibold uppercase tracking-wide text-ink-muted">Document</h5>
      <div className="mt-2 flex items-start gap-3">
        <FileText className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="break-words text-sm font-medium text-ink">{document.fileName}</p>
          <p className="text-xs text-ink-muted">
            Uploaded {shortDate(document.uploadedAt)} · {DOCUMENT_STATUS_TEXT[document.status]}
          </p>
          {failed && (
            <p role="alert" className="mt-1 text-xs text-red-600 dark:text-error-dark">
              {DOWNLOAD_FAILED_TEXT}
            </p>
          )}
        </div>
        {document.downloadable && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="shrink-0"
            aria-label={`Download ${document.fileName}`}
            loading={downloading}
            onClick={() => void download()}
          >
            {!downloading && <Download className="h-4 w-4" aria-hidden />}
          </Button>
        )}
      </div>
    </div>
  );
}
